import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  ListItemText as MuiListItemText,
  Chip,
  Card,
  CardContent,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  EditOutlined as EditOutlinedIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import MasterDataDrawer from './MasterDataDrawer';

// Import user rights context
import { useUserRights } from '../../../context/UserRightsContext';

import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateToolbarButtonSx,
  harmonateBlueButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDialogCloseIconSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateSwitchSx,
  harmonateGridActionIconSx,
} from '../../../theme/harmonateStyles';

// Import audit logger
import { AuditLoggers } from '../../../utils/auditLogger';

// Toast implementation with queue
const useToast = () => {
  const [toastQueue, setToastQueue] = useState([]);

  const showSuccess = (message, title = 'Success') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'success', title }]);
  };

  const showError = (message, title = 'Error') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'error', title }]);
  };

  const showInfo = (message, title = 'Info') => {
    setToastQueue(prev => [...prev, { open: true, message, severity: 'info', title }]);
  };

  const hideToast = () => {
    setToastQueue(prev => prev.slice(1));
  };

  useEffect(() => {
    if (toastQueue.length > 0) {
      const timer = setTimeout(() => {
        hideToast();
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [toastQueue]);

  return { toastState: toastQueue[0] || { open: false, message: '', severity: 'success', title: '' }, showSuccess, showError, showInfo, hideToast };
};

// Utility function to get tenant_id
const getTenantId = async () => {
  return localStorage.getItem('tenant_id');
};

const MasterData = () => {
  // Use user rights context
  const { hasPermission, isHarmonateUser } = useUserRights();

  // Entity tab state
  const [tabValue, setTabValue] = useState('fund');
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState(null);
  
  // Duplicate scan state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);

  // Orphaned workflows check state
  const [orphanedWorkflowsDialogOpen, setOrphanedWorkflowsDialogOpen] = useState(false);
  const [orphanedWorkflowsResults, setOrphanedWorkflowsResults] = useState(null);
  const [checkingOrphanedWorkflows, setCheckingOrphanedWorkflows] = useState(false);

  // Fetch entities for mapping
  const fetchEntities = async () => {
    try {
      setEntitiesLoading(true);
      setEntitiesError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/'}conductor/entity-master`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`Failed to fetch entities: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setEntities(Array.isArray(data) ? data : []);
    } catch (err) {
      setEntitiesError(err.message);
      setEntities([]);
    } finally {
      setEntitiesLoading(false);
    }
  };

  // Get entity ID based on tab value
  const getEntityIdForTab = (tab) => {
    if (tab === 'fund') {
      const fundEntity = entities.find(e => e.entityName?.toLowerCase() === 'fund');
      return fundEntity?.entityId || '';
    } else if (tab === 'investor') {
      const investorEntity = entities.find(e => e.entityName?.toLowerCase() === 'investor');
      return investorEntity?.entityId || '';
    }
    return '';
  };

  // Initialize entities on component mount
  useEffect(() => {
    fetchEntities();
  }, []);

  // const handleTabChange = (event, newValue) => {
  //   setTabValue(newValue);
  //   if (newValue === 'fund') {
  //     fetchFunds();
  //   } else {
  //     fetchInvestors();
  //   }
  // };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm(''); // Clear search when switching tabs
    if (newValue === 'fund') {
      fetchFunds();
    } else {
      fetchInvestors();
    }
  };

  // Main data states
  const [funds, setFunds] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [editingFund, setEditingFund] = useState(null);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data based on active tab
  const fetchData = async () => {
    if (tabValue === 'fund') {
      await fetchFunds();
    } else {
      await fetchInvestors();
    }
  };

  // Fetch funds data from API
  const fetchFunds = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();

      if (!token || !tenantId) {
        throw new Error('Authentication error');
      }

      const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/'}conductor/funds/by-tenant-entity?tenantId=${tenantId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch funds data: ${response.status} ${response.statusText}`);

      const data = await response.json();
      const transformedData = (Array.isArray(data) ? data : []).map((item, idx) => ({
        id: item.masterFundId || item.id || idx + 1,
        fundName: item.fundName || '',
        fundCode: item.fundCode || '',
        isMultiInvestor: item.isMultiInvestor === true || item.isMultiInvestor === 'true',
        ...item
      }));

      setFunds(transformedData);

      if (transformedData.length === 0) {
        showInfo('No funds found', 'Empty Results');
      } else {
        showSuccess(`Successfully loaded ${transformedData.length} funds`, 'Data Loaded');
      }
    } catch (err) {
      console.error('Error fetching funds:', err);
      setError(err.message);
      setFunds([]);
      showError(`Failed to load funds: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch investors data from API
  const fetchInvestors = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();

      if (!token || !tenantId) {
        const errorMsg = `Authentication error - Token: ${!!token}, TenantId: ${tenantId}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/investors/tenant/${tenantId}`;

      console.log('Fetching investors with URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch investors: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      const transformedData = (Array.isArray(data) ? data : []).map((item, idx) => ({
        id: item.investorId || item.id || idx + 1,
        investorName: item.investorName || '',
        investorCode: item.investorCode || '',
        ...item
      }));

      setInvestors(transformedData);

      if (transformedData.length === 0) {
        showInfo('No investors found', 'Empty Results');
      } else {
        showSuccess(`Successfully loaded ${transformedData.length} investors`, 'Data Loaded');
      }
    } catch (err) {
      console.error('Error in fetchInvestors:', err);
      setError(err.message);
      setInvestors([]);
      showError(`Failed to load investors: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  // Add new investor
  const handleAddInvestor = async (investorData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();

      if (!token || !tenantId) {
        throw new Error('Authentication error');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/'}conductor/investors?tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          investorName: investorData.name,
          investorCode: investorData.code
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add investor');
      }

      showSuccess('Investor added successfully', 'Success');
      await fetchInvestors();
      return true;
    } catch (error) {
      console.error('Error adding investor:', error);
      showError(error.message || 'Failed to add investor', 'Error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update investor
  const handleUpdateInvestor = async (id, investorData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();

      if (!token || !tenantId) {
        throw new Error('Authentication error');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/'}conductor/investors/${id}?tenantId=${encodeURIComponent(tenantId)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          investorName: investorData.name,
          investorCode: investorData.code
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update investor');
      }

      showSuccess('Investor updated successfully', 'Success');
      await fetchInvestors();
      return true;
    } catch (error) {
      console.error('Error updating investor:', error);
      showError(error.message || 'Failed to update investor', 'Error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Upload dialog states
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);

  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFundName, setEditFundName] = useState('');
  const [editFundCode, setEditFundCode] = useState('');
  const [editTenantId, setEditTenantId] = useState('');
  const [editIsMultiInvestor, setEditIsMultiInvestor] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // Add fund dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFundName, setAddFundName] = useState('');
  const [addFundCode, setAddFundCode] = useState('');
  const [addIsMultiInvestor, setAddIsMultiInvestor] = useState(false);
  const [addEntityId, setAddEntityId] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState(null);

  // Menu dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Use the toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // Excel export functionality
  // const handleExportToExcel = () => {
  //   try {
  //     if (!funds || funds.length === 0) {
  //       showError('No data to export', 'Export Error');
  //       return;
  //     }

  //     const exportData = funds.map(fund => ({
  //       'Fund Name': fund.fundName || '',
  //       'Fund Code': fund.fundCode || '',
  //       'Master Fund ID': fund.masterFundId || '',
  //       'Entity ID': fund.entityId || '',
  //       'Tenant ID': fund.tenantId || '',
  //       'Created Date': fund.createdAt ? new Date(fund.createdAt).toLocaleDateString() : '',
  //       'Updated Date': fund.updatedAt ? new Date(fund.updatedAt).toLocaleDateString() : '',
  //     }));

  //     const workbook = XLSX.utils.book_new();
  //     const worksheet = XLSX.utils.json_to_sheet(exportData);

  //     const columnWidths = [];
  //     const headers = Object.keys(exportData[0] || {});

  //     headers.forEach((header, index) => {
  //       const maxLength = Math.max(
  //         header.length,
  //         ...exportData.map(row => String(row[header] || '').length)
  //       );
  //       columnWidths[index] = { wch: Math.min(maxLength + 2, 50) };
  //     });

  //     worksheet['!cols'] = columnWidths;

  //     XLSX.utils.book_append_sheet(workbook, worksheet, 'Master Data');

  //     const timestamp = new Date().toISOString().split('T')[0];
  //     const filename = `Master_Data_${timestamp}.xlsx`;

  //     XLSX.writeFile(workbook, filename);

  //     showSuccess(`Successfully exported ${funds.length} records to ${filename}`, 'Export Complete');
  //   } catch (error) {
  //     console.error('Error exporting to Excel:', error);
  //     showError(`Failed to export data: ${error.message}`, 'Export Error');
  //   }
  // };
  // Excel export functionality
  const handleExportToExcel = () => {
    if (!isHarmonateUser && !hasPermission('Master Data', 'export')) return;
    try {
      const currentData = tabValue === 'fund' ? filteredData : filteredData;

      if (!currentData || currentData.length === 0) {
        showError('No data to export', 'Export Error');
        return;
      }

      let exportData;
      if (tabValue === 'fund') {
        exportData = currentData.map(fund => ({
          'Funds ID': fund.masterFundId ?? fund.id ?? '',
          'Fund Name': fund.fundName || '',
          'Fund Code': fund.fundCode || '',
        }));
      } else {
        exportData = currentData.map(investor => ({
          'Investor Name': investor.investorName || '',
          'Investor Code': investor.investorCode || '',
        }));
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [];
      const headers = Object.keys(exportData[0] || {});

      headers.forEach((header, index) => {
        const maxLength = Math.max(
          header.length,
          ...exportData.map(row => String(row[header] || '').length)
        );
        columnWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });

      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, tabValue === 'fund' ? 'Funds' : 'Investors');

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${tabValue === 'fund' ? 'Funds' : 'Investors'}_${timestamp}.xlsx`;

      XLSX.writeFile(workbook, filename);

      showSuccess(`Successfully exported ${currentData.length} records to ${filename}`, 'Export Complete');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showError(`Failed to export data: ${error.message}`, 'Export Error');
    }
  };
  // Import funds
  const handleImport = async () => {
    if (!selectedFile) {
      showError('Please select a file to import', 'No File Selected');
      return;
    }

    const tenantId = await getTenantId();
    if (!tenantId) {
      showError('Tenant ID not found', 'Authentication Error');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found', 'Authentication Error');
      return;
    }

    // Validate file extension
    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      showError('Invalid file type. Please upload a CSV or Excel file.', 'Invalid File Type');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');

      // Use different endpoints based on the active tab
      const endpoint = tabValue === 'fund' ? 'funds/import' : 'investors/import';
      const url = `${baseUrl}/conductor/${endpoint}?tenantId=${encodeURIComponent(tenantId)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Failed to import ${tabValue === 'fund' ? 'funds' : 'investors'}: ${response.status} ${response.statusText}`;

        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (typeof errorData === 'string') {
              errorMessage = errorData;
            }
          } else {
            const errorText = await response.text();
            if (errorText && !errorText.includes('<html>') && errorText.length < 500) {
              errorMessage = errorText;
            }
          }
        } catch (parseError) {
          console.error('Could not parse error response:', parseError);
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

    // Log the import action in audit log
    try {
      if (tabValue === 'fund') {
        await AuditLoggers.fundImported(selectedFile.name);
      } else {
        await AuditLoggers.investorImported(selectedFile.name);
      }
    } catch (logError) {
      console.error('Error logging audit trail:', logError);
    }

    showSuccess(`${tabValue === 'fund' ? 'Funds' : 'Investors'} imported successfully`, 'Import Complete');

    // Refresh the data
      if (tabValue === 'fund') {
        await fetchFunds();
      } else {
        await fetchInvestors();
      }

      // Show success messages
      if (tabValue === 'fund') {
        if (result?.skippedFundNames?.length > 0) {
          showInfo(
            `The following funds were skipped as duplicates: ${result.skippedFundNames.join(', ')}`,
            'Duplicate Funds Skipped'
          );
        }

        if (result?.skippedParserNames?.length > 0) {
          showInfo(
            `The following parsers were skipped as duplicates: ${result.skippedParserNames.join(', ')}`,
            'Duplicate Parsers Skipped'
          );
        }
      } else if (result?.skipped?.length > 0) {
        showInfo(
          `The following investors were skipped as duplicates: ${result.skipped.join(', ')}`,
          'Duplicate Investors Skipped'
        );
      }

      // Close the dialog on success
      handleCloseUploadDialog();
    } catch (error) {
      console.error('Error importing data:', error);
      showError(error.message || 'An unexpected error occurred during import', 'Import Error');
    } finally {
      setImporting(false);
    }
  };

  // Handle edit fund
  const handleEditItem = (item) => {
    if (tabValue === 'fund') {
      setEditingFund(item);
      setEditFundName(item.fundName || '');
      setEditFundCode(item.fundCode || '');
      setEditTenantId(item.tenantId || '');
      setEditIsMultiInvestor(item.isMultiInvestor === true || item.isMultiInvestor === 'true');
      setEditDialogOpen(true);
      setEditError(null);
    } else {
      setEditingInvestor(item);
      setEditFundName(item.investorName || '');
      setEditFundCode(item.investorCode || '');
      setEditDialogOpen(true);
    }
  };

  // Handle edit submit
  const handleSaveEdit = async () => {
    if (tabValue === 'fund') {
      if (!editingFund || !editFundName.trim() || !editFundCode.trim()) {
        setEditError('Fund name and code are required');
        return;
      }

      setEditLoading(true);
      setEditError(null);

      try {
        const token = localStorage.getItem('token');
        const tenantId = await getTenantId();

        if (!token) throw new Error('No authentication token found');
        if (!tenantId) throw new Error('No tenant ID found');

        const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
        const url = `${baseUrl}/conductor/funds/${editingFund.id}?tenantId=${encodeURIComponent(tenantId)}`;

        const payload = {
          fundName: editFundName.trim(),
          fundCode: editFundCode.trim(),
          isMultiInvestor: Boolean(editIsMultiInvestor)
        };

        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
        }

        // Log the update action in audit log
        try {
          await AuditLoggers.fundUpdated(editingFund.fundName, editingFund.id);
        } catch (logError) {
          console.error('Error logging audit trail:', logError);
        }

        showSuccess('Fund updated successfully', 'Update Complete');
        setEditDialogOpen(false);
        setEditingFund(null);
        setEditFundName('');
        setEditFundCode('');
        setEditTenantId('');
        setEditIsMultiInvestor(false);

        // Refresh the data to ensure updated values are displayed
        setTimeout(async () => {
          await fetchFunds();
          // After fetch, check if the edited fund is present
          if (editingFund && editingFund.id) {
            const found = funds.find(f => f.id === editingFund.id);
            if (!found) {
              showInfo('Edited fund no longer matches current filters and may have been filtered out.', 'Fund Not Visible');
            }
          }
        }, 500);

      } catch (err) {
        console.error('Error updating fund:', err);
        setEditError(err.message || 'Failed to update fund');
      } finally {
        setEditLoading(false);
      }
    } else {
      if (!editingInvestor || !editFundName.trim() || !editFundCode.trim()) {
        setEditError('Investor name and code are required');
        return;
      }

      setEditLoading(true);
      setEditError(null);

      try {
        const success = await handleUpdateInvestor(editingInvestor.id, {
          name: editFundName.trim(),
          code: editFundCode.trim()
        });

        if (success) {
          // Log the update action in audit log for investor
          try {
            await AuditLoggers.investorUpdated(editingInvestor.investorName, editingInvestor.id);
          } catch (logError) {
            console.error('Error logging audit trail for investor update:', logError);
          }
          
          setEditDialogOpen(false);
        }
      } catch (err) {
        console.error('Error updating investor:', err);
        setEditError(err.message || 'Failed to update investor');
      } finally {
        setEditLoading(false);
      }
    }
  };

  // Handle row click
  const handleRowClick = (params) => {
    console.log('Row clicked:', params.row); // Debug log
    setSelectedFund(params.row);
    setDrawerOpen(true);
    console.log('Drawer state:', { selectedFund: params.row, drawerOpen: true }); // Debug log
  };

  const handleOpenUploadDialog = () => {
    if (!isHarmonateUser && !hasPermission('Master Data', 'upload')) return;
    setOpenUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
    setSelectedFile(null);
    setDragActive(false);
  };

  const handleRetry = () => {
    fetchFunds();
  };

  // Drag and drop functionality
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      showInfo(`Selected file: ${file.name}`);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      showInfo(`Selected file: ${file.name}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // DataGrid columns with Edit action
  const columns = [
    {
      field: 'fundName',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fundCode',
      headerName: 'Code',
      flex: 1,
      minWidth: 150,
    },
    // {
    //   field: 'isMultiInvestor',
    //   headerName: 'Is Multi Invester',
    //   flex: 1,
    //   minWidth: 180,
    //   valueGetter: (params) => {
    //     // Handle both boolean and string values
    //     const value = params.row.isMultiInvestor;
    //     if (typeof value === 'boolean') {
    //       return value ? 'Yes' : 'No';
    //     }
    //     return value === 'true' || value === true ? 'Yes' : 'No';
    //   },
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleEditItem(params.row);
          }}
          size="small"
          disabled={!isHarmonateUser && !hasPermission('Master Data', 'edit')}
          sx={{
            ...harmonateGridActionIconSx,
            ...((!isHarmonateUser && !hasPermission('Master Data', 'edit')) && {
              color: ht.fadedText,
              '&:hover': {
                bgcolor: 'transparent',
                color: ht.fadedText,
              },
            }),
          }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportFromMenu = () => {
    handleExportToExcel();
    handleMenuClose();
  };

  const handleImportFromMenu = () => {
    handleOpenUploadDialog();
    handleMenuClose();
  };

  // Search functionality
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle scan duplicates
  const handleScanDuplicates = async () => {
    try {
      setScanningDuplicates(true);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/funds/tenant/${tenantId}/scan-duplicates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to scan duplicates: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      const data = await resp.json();
      setDuplicateResults(data);
      setDuplicateDialogOpen(true);
      showSuccess('Duplicate scan completed successfully');
    } catch (err) {
      console.error('Error scanning duplicates:', err);
      showError(err.message, 'Scan Duplicates Error');
    } finally {
      setScanningDuplicates(false);
    }
  };

  // Handle check orphaned workflows
  const handleCheckOrphanedWorkflows = async () => {
    try {
      setCheckingOrphanedWorkflows(true);
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      // Ensure proper URL construction with trailing slash
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');
      const url = `${baseUrl}/conductor/extraction-configurations/check-orphaned-workflows?tenantId=${tenantId}`;
      
      const resp = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to check orphaned workflows: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      const data = await resp.json();
      setOrphanedWorkflowsResults(data);
      setOrphanedWorkflowsDialogOpen(true);
      showSuccess('Orphaned workflows check completed successfully');
    } catch (err) {
      console.error('Error checking orphaned workflows:', err);
      showError(err.message, 'Check Orphaned Workflows Error');
    } finally {
      setCheckingOrphanedWorkflows(false);
    }
  };

  // Filter funds based on search term
  // const filteredFunds = funds.filter(fund => 
  //   fund.fundName.toLowerCase().includes(searchTerm.toLowerCase())
  // );
  // Filter data based on search term and active tab
  const filteredData = React.useMemo(() => {
    if (tabValue === 'fund') {
      return funds.filter(fund =>
        fund.fundName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        fund.fundCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } else {
      return investors.filter(investor =>
        investor.investorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        investor.investorCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  }, [funds, investors, searchTerm, tabValue]);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading master data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load master data
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRetry}
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const handleOpenAddDialog = () => {
    // Check if user has add permission for Master Data function
    if (!isHarmonateUser && !hasPermission('Master Data', 'add')) {
      showError('You do not have permission to add master data', 'Access Denied');
      return;
    }

    setAddDialogOpen(true);
    setAddFundName('');
    setAddFundCode('');
    setAddIsMultiInvestor(false);
    setAddEntityId('');
    setAddError(null);
  };
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddFundName('');
    setAddFundCode('');
    setAddIsMultiInvestor(false);
    setAddEntityId('');
    setAddError(null);
  };
  const handleAddFund = async (e) => {
    e.preventDefault();

    if (!addFundName.trim() || !addFundCode.trim()) {
      setAddError('Please fill in all required fields');
      return;
    }

    setAddSubmitting(true);
    setAddError(null);

    try {
      const token = localStorage.getItem('token');
      const tenantId = await getTenantId();

      if (!token || !tenantId) {
        throw new Error('Authentication error');
      }

      const baseUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:4002/').replace(/\/+$/, '');

      if (tabValue === 'fund') {
        // Save to funds API
        const url = `${baseUrl}/conductor/funds?tenantId=${encodeURIComponent(tenantId)}`;
        const payload = {
          tenantId: tenantId,
          fundName: addFundName.trim(),
          fundCode: addFundCode.trim(),
          isMultiInvestor: Boolean(addIsMultiInvestor)
        };

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add fund');
        }

        // Log successful fund addition using centralized logger
        await AuditLoggers.fundAdded(addFundName.trim());
      } else {
        // Save to investors API
        const url = `${baseUrl}/conductor/investors?tenantId=${encodeURIComponent(tenantId)}`;
        const payload = {
          investorName: addFundName.trim(),
          investorCode: addFundCode.trim(),
        };

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add investor');
        }

        // Log successful investor addition using centralized logger
        await AuditLoggers.investorAdded(addFundName.trim());
      }

      // Show success message and refresh data
      showSuccess(
        tabValue === 'fund' ? 'Fund added successfully' : 'Investor added successfully',
        'Success'
      );

      handleCloseAddDialog();
      fetchData(); // Refresh the current tab's data
    } catch (error) {
      console.error('Error adding item:', error);
      setAddError(error.message || 'An error occurred while adding the item');
    } finally {
      setAddSubmitting(false);
    }
  };

  // const renderDataGrid = () => {
  //   const commonProps = {
  //     loading,
  //     rowsPerPageOptions: [10, 25, 50],
  //     pagination: true,
  //     disableSelectionOnClick: true,
  //     components: {
  //       LoadingOverlay: LinearProgress,
  //     },
  //     sx: {
  //       '& .MuiDataGrid-columnHeaders': {
  //         backgroundColor: '#f5f5f5',
  //       },
  //     },
  //   };

  //   if (tabValue === 'fund') {
  //     return (
  //       <DataGridPro
  //         rows={funds}
  //         columns={columns}
  //         onRowClick={handleRowClick}
  //         {...commonProps}
  //       />
  //     );
  //   } else {
  //     const investorColumns = [
  //       { field: 'investorName', headerName: 'Investor Name', flex: 1 },
  //       { field: 'investorCode', headerName: 'Investor Code', flex: 1 },
  //       {
  //         field: 'actions',
  //         headerName: 'Actions',
  //         sortable: false,
  //         width: 120,
  //         renderCell: (params) => (
  //           <IconButton
  //             onClick={(e) => {
  //               e.stopPropagation();
  //               handleEditItem(params.row);
  //             }}
  //           >
  //             <EditIcon />
  //           </IconButton>
  //         ),
  //       },
  //     ];

  //     return (
  //       <DataGridPro
  //         rows={investors}
  //         columns={investorColumns}
  //         {...commonProps}
  //       />
  //     );
  //   }
  // };

  const renderDataGrid = () => {
    const commonProps = {
      loading,
      rowsPerPageOptions: [10, 25, 50],
      pagination: true,
      disableSelectionOnClick: true,
      components: {
        LoadingOverlay: LinearProgress,
      },
      sx: {
        ...harmonateDataGridSx,
        flex: 1,
        minHeight: 0,
        height: '100%',
      },
    };

    if (tabValue === 'fund') {
      return (
        <DataGridPro
          rows={filteredData}
          columns={columns}
          onRowClick={handleRowClick}
          columnResizeMode="onEnd"
          resizable
          {...commonProps}
        />
      );
    } else {
      const investorColumns = [
        { field: 'investorName', headerName: 'Investor Name', flex: 1 },
        { field: 'investorCode', headerName: 'Investor Code', flex: 1 },
        {
          field: 'actions',
          headerName: 'Actions',
          sortable: false,
          width: 120,
          renderCell: (params) => (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEditItem(params.row);
              }}
              size="small"
              disabled={!isHarmonateUser && !hasPermission('Master Data', 'edit')}
              sx={{
                ...harmonateGridActionIconSx,
                ...((!isHarmonateUser && !hasPermission('Master Data', 'edit')) && {
                  color: ht.fadedText,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: ht.fadedText,
                  },
                }),
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          ),
        },
      ];

      return (
        <DataGridPro
          rows={filteredData}
          columns={investorColumns}
          columnResizeMode="onEnd"
          resizable
          {...commonProps}
        />
      );
    }
  };
  return (
    <>
      <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          mb: 3,
        }}>
            <Typography variant="h6" sx={harmonatePageTitleSx}>
              Master Data
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={scanningDuplicates ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SearchIcon />}
                onClick={handleScanDuplicates}
                disabled={scanningDuplicates || checkingOrphanedWorkflows}
                sx={harmonateToolbarButtonSx}
              >
                Scan Duplicate ID Terms
              </Button>
              <Button
                variant="outlined"
                startIcon={checkingOrphanedWorkflows ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <SearchIcon />}
                onClick={handleCheckOrphanedWorkflows}
                disabled={scanningDuplicates || checkingOrphanedWorkflows}
                sx={harmonateToolbarButtonSx}
              >
                Check Orphaned Workflows
              </Button>
              <IconButton
                onClick={handleOpenAddDialog}
                disabled={!isHarmonateUser && !hasPermission('Master Data', 'add')}
                sx={harmonatePlainIconButtonSx}
              >
                <AddIcon />
              </IconButton>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={handleSearchChange}
                sx={harmonatePageSearchFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={harmonatePageSearchIconSx} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                onClick={handleMenuOpen}
                sx={harmonatePlainIconButtonSx}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    borderRadius: ht.cardRadius,
                    boxShadow: ht.cardShadow,
                    minWidth: 180,
                  }
                }}
              >
                <MenuItem onClick={handleExportFromMenu} disabled={!filteredData || filteredData.length === 0 || (!isHarmonateUser && !hasPermission('Master Data', 'export'))}>
                  <ListItemIcon>
                    <DownloadIcon fontSize="small" sx={{ color: ht.text }} />
                  </ListItemIcon>
                  <ListItemText>Export to Excel</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleImportFromMenu} disabled={!isHarmonateUser && !hasPermission('Master Data', 'upload')}>
                  <ListItemIcon>
                    <UploadIcon fontSize="small" sx={{ color: ht.green }} />
                  </ListItemIcon>
                  <ListItemText>Import {tabValue === 'fund' ? 'Funds' : 'Investors'}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
        </Box>

        {/* Data Grid */}
        <Paper elevation={0} sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {renderDataGrid()}
        </Paper>

        {/* Enhanced Upload Dialog */}
        <Dialog
          open={openUploadDialog}
          onClose={!importing ? handleCloseUploadDialog : undefined}
          maxWidth="sm"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              Import {tabValue === 'fund' ? 'Funds' : 'Investors'}
              <IconButton
                onClick={handleCloseUploadDialog}
                disabled={importing}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={harmonateDialogContentSx}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Required Columns:</Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {tabValue === 'fund' ? (
                  <>
                    <li>Fund Name</li>
                    <li>Fund Code</li>
                  </>
                ) : (
                  <>
                    <li>Investor Name</li>
                    <li>Investor Code</li>
                  </>
                )}
              </ul>

              {tabValue === 'fund' && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Optional Columns:</Typography>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Parser Name</li>
                    <li>Parser ID</li>
                    <li>Parser System</li>
                    <li>Document Type</li>
                    <li>Workflow Name</li>
                    <li>Include Terms</li>
                    <li>Exclude Terms</li>
                  </ul>
                </>
              )}

              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {tabValue === 'fund'
                  ? 'The file must include the required headers and may optionally include any of the optional headers. Column order doesn\'t matter.'
                  : 'The file must include the required headers. Column order doesn\'t matter.'
                }
              </Typography>

              {tabValue === 'fund' && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Each Include and Exclude Terms need to be separated by Pipe(|) Separately.
                </Typography>
              )}

            </Alert>
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: dragActive ? `2px dashed ${ht.blue}` : `2px dashed ${ht.line}`,
                borderRadius: ht.fieldRadius,
                p: 4,
                textAlign: 'center',
                bgcolor: dragActive ? 'rgba(46, 166, 160, 0.08)' : ht.listBg,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: ht.darkBlue,
                  bgcolor: 'rgba(1, 48, 98, 0.04)',
                },
              }}
            >
              <CloudUploadIcon
                sx={{
                  fontSize: 64,
                  color: dragActive ? ht.blue : ht.fadedText,
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ mb: 1, color: ht.text }}>
                {selectedFile ? selectedFile.name : 'Drop your file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedFile ?
                  `File size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` :
                  'or click to browse files'
                }
              </Typography>
              <input
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                id="file-upload-input"
                type="file"
                onChange={handleFileSelect}
                disabled={importing}
              />
              <label htmlFor="file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={importing}
                  sx={harmonateUploadOutlinedSx}
                >
                  Browse Files
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                Supported formats: .xlsx, .xls, .csv
              </Typography>
            </Box>
            {importing && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: ht.line,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: ht.green,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                  Importing {tabValue === 'fund' ? 'funds' : 'investors'}...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseUploadDialog}
              disabled={importing}
              variant="outlined"
              sx={harmonateCancelButtonSx}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={!selectedFile || importing}
              sx={harmonateProceedButtonSx}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Fund Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              {tabValue === 'fund' ? 'Edit Fund' : 'Edit Investor'}
              <IconButton
                onClick={() => setEditDialogOpen(false)}
                disabled={editLoading}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={editFundName}
                onChange={(e) => setEditFundName(e.target.value)}
                disabled={editLoading}
                fullWidth
                size="small"
                required
                error={editError && !editFundName.trim()}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />

              <TextField
                label="Code"
                value={editFundCode}
                onChange={(e) => setEditFundCode(e.target.value)}
                disabled={editLoading}
                fullWidth
                size="small"
                required
                error={editError && !editFundCode.trim()}
                InputLabelProps={harmonateInputLabelProps}
                sx={harmonateTextFieldSx}
              />

              {/* {tabValue === 'fund' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={editIsMultiInvestor}
                      onChange={(e) => setEditIsMultiInvestor(e.target.checked)}
                      disabled={editLoading}
                      sx={harmonateSwitchSx}
                    />
                  }
                  label="Is Multi Investor"
                />
              )} */}

              {editError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {editError}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              disabled={editLoading}
              variant="outlined"
              sx={harmonateCancelButtonSx}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              disabled={editLoading || !editFundName.trim() || !editFundCode.trim()}
              sx={harmonateProceedButtonSx}
            >
              {editLoading ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Fund Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={!addSubmitting ? handleCloseAddDialog : undefined}
          maxWidth="sm"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              Add New {tabValue === 'fund' ? 'Fund' : 'Investor'}
              <IconButton
                onClick={handleCloseAddDialog}
                disabled={addSubmitting}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <form onSubmit={handleAddFund}>
            <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Name"
                  value={addFundName}
                  onChange={(e) => setAddFundName(e.target.value)}
                  disabled={addSubmitting}
                  fullWidth
                  size="small"
                  required
                  error={addError && !addFundName.trim()}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />

                <TextField
                  label="Code"
                  value={addFundCode}
                  onChange={(e) => setAddFundCode(e.target.value)}
                  disabled={addSubmitting}
                  fullWidth
                  size="small"
                  required
                  error={addError && !addFundCode.trim()}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />

                {/* {tabValue === 'fund' && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={addIsMultiInvestor}
                        onChange={(e) => setAddIsMultiInvestor(e.target.checked)}
                        disabled={addSubmitting}
                        sx={harmonateSwitchSx}
                      />
                    }
                    label="Is Multi Investor"
                  />
                )} */}

                {addError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {addError}
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={handleCloseAddDialog}
                disabled={addSubmitting}
                variant="outlined"
                sx={harmonateCancelButtonSx}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={addSubmitting || !addFundName.trim() || !addFundCode.trim()}
                sx={harmonateProceedButtonSx}
              >
                {addSubmitting ? 'Creating...' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Master Data Drawer */}
        <MasterDataDrawer
          open={drawerOpen}
          onClose={() => {
            console.log('Closing drawer'); // Debug log
            setDrawerOpen(false);
            setSelectedFund(null);
          }}
          fundData={selectedFund}
        />

        {/* Duplicate Terms Dialog */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>Duplicate Terms Scan Results</DialogTitle>
          <DialogContent sx={harmonateDialogContentSx} dividers>
            {duplicateResults ? (
              Object.keys(duplicateResults).length > 0 ? (
                <Stack spacing={2}>
                  {Object.entries(duplicateResults).map(([term, locations]) => (
                    <Card key={term} variant="outlined" sx={{ borderLeft: `4px solid ${ht.blue}` }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: ht.text }}>
                          Term: "{term}"
                        </Typography>
                        <Typography variant="body2" sx={{ color: ht.text, mb: 2 }} gutterBottom>
                          Found in {locations.length} fund(s):
                        </Typography>
                        <List dense>
                          {locations.map((location, index) => (
                            <React.Fragment key={index}>
                              <ListItem>
                                <MuiListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: ht.text }}>
                                        {location.fundName}
                                      </Typography>
                                      <Chip
                                        label={location.termType === 'include' ? 'Include' : 'Exclude'}
                                        size="small"
                                        sx={{
                                          bgcolor: location.termType === 'include' ? 'rgba(2, 107, 29, 0.12)' : 'rgba(239, 63, 63, 0.12)',
                                          color: location.termType === 'include' ? ht.green : ht.red,
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          borderRadius: ht.buttonRadius,
                                        }}
                                      />
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {index < locations.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">
                  No duplicate terms found. All terms are unique across funds.
                </Alert>
              )
            ) : (
              <Typography variant="body2" sx={{ color: ht.text }}>
                No results available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDuplicateDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Orphaned Workflows Dialog */}
        <Dialog
          open={orphanedWorkflowsDialogOpen}
          onClose={() => setOrphanedWorkflowsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={harmonateDialogPaperProps}
        >
          <DialogTitle sx={harmonateDialogTitleSx}>Orphaned Workflows Check Results</DialogTitle>
          <DialogContent sx={harmonateDialogContentSx} dividers>
            {orphanedWorkflowsResults ? (
              Array.isArray(orphanedWorkflowsResults) && orphanedWorkflowsResults.length > 0 ? (
                <Stack spacing={2}>
                  <Alert severity="warning" sx={{ mb: 2, borderColor: ht.blue, color: ht.text }}>
                    Found {orphanedWorkflowsResults.length} item(s) that need attention:
                    <br />
                    • Workflows not configured for this tenant
                    <br />
                    • Funds not configured with document types and workflows
                  </Alert>
                  {orphanedWorkflowsResults.map((item, index) => {
                    const hasWorkflow = item.workflowDefinitionSnowflakeId !== null && item.workflowDefinitionSnowflakeId !== undefined;
                    const isUnconfiguredFund = !hasWorkflow;
                    
                    return (
                      <Card 
                        key={index} 
                        variant="outlined" 
                        sx={{ 
                          borderLeft: `4px solid ${isUnconfiguredFund ? ht.red : ht.blue}`,
                        }}
                      >
                        <CardContent>
                          {hasWorkflow ? (
                            <>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: ht.text }}>
                                {item.workflowName || `Workflow ${index + 1}`}
                              </Typography>
                              {item.fundName && (
                                <Typography variant="body2" sx={{ color: ht.text }}>
                                  <strong>Fund Name:</strong> {item.fundName}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: ht.red }}>
                                Unconfigured Fund
                              </Typography>
                              {item.fundName && (
                                <Typography variant="body2" sx={{ color: ht.text }} gutterBottom>
                                  <strong>Fund Name:</strong> {item.fundName}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ color: ht.text, mt: 1, fontStyle: 'italic' }}>
                                This fund has not been configured with any document type and workflow in the side drawer.
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="success">
                  No orphaned workflows found. All workflows are properly associated with document types and all funds are configured.
                </Alert>
              )
            ) : (
              <Typography variant="body2" sx={{ color: ht.text }}>
                No results available.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOrphanedWorkflowsDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast notification */}
        {toastState.open && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 9999,
              maxWidth: 400
            }}
          >
            <Alert
              severity={toastState.severity}
              onClose={hideToast}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">{toastState.title}</Typography>
              <Typography variant="body2">{toastState.message}</Typography>
            </Alert>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MasterData;